// Store for UI element state (open drawers/modals, etc.)
import type { RoomPanelType } from '@/helpers/panelMap'
import { createResizeObserver } from '@/helpers/utils'
import type { MutableRefObject, ReactNode } from 'react'
import { create, type StateCreator } from 'zustand'

type SetErrorDialogParams =
  | {
      cancelLabel: ReactNode
      confirmLabel: ReactNode
      description: ReactNode
      onCancel?: () => void
      onConfirm: () => void
      title: ReactNode
    }
  | {
      cancelLabel?: never
      confirmLabel: ReactNode
      description: ReactNode
      onCancel?: never
      onConfirm: () => void
      title: ReactNode
    }

interface Actions {
  actions: {
    closeRoomPanel: () => void
    createRootElementResizeObserver: (element: HTMLElement) => {
      start: () => void
      stop: () => void
    }
    hideCallEndDialog: () => void
    hideContact: () => void
    hideErrorDialog: () => void
    openRoomPanel: () => void
    resetRoomPanelState: () => void
    setAdvancedPanel: (memberId: string) => void
    setCallEndDialogContent: ({
      description,
      onConfirm,
    }: {
      description: ReactNode
      onConfirm?: (() => void) | undefined
    }) => void
    setErrorDialogContent: ({
      cancelLabel,
      confirmLabel,
      description,
      onCancel,
      onConfirm,
      title,
    }: SetErrorDialogParams) => void
    setErrorDialogRef: (ref: HTMLDivElement | null) => void
    setRoomPanelTitle: (panelTitle: string) => void
    setRoomPanelType: (panelType: RoomPanelType) => void
    setRootElementRef: (element: HTMLElement | null) => void
    setViewElementRef: (ref: MutableRefObject<HTMLElement | null>) => void
    showCallEndDialog: () => void
    showContact: () => void
    showErrorDialog: () => void
  }
}

interface State {
  callEndDialogDescription: ReactNode
  callEndDialogOnConfirm?: (() => void) | undefined
  displayCallEndDialog: boolean
  displayContact: boolean
  displayErrorDialog: boolean
  errorCancelLabel: ReactNode
  errorConfirmLabel: ReactNode
  errorDescription: ReactNode
  errorDialogElementRef: HTMLDivElement | null
  errorOnClose: (() => void) | undefined
  errorOnConfirm: (() => void) | undefined
  errorTitle: ReactNode
  roomPanelOpen: boolean
  roomPanelTitle: string
  roomPanelType: RoomPanelType | null
  rootElementHeight: number
  rootElementRef: HTMLElement | null
  rootElementWidth: number
  selectedMemberId: string
  viewElementRef: MutableRefObject<HTMLElement | null> | null
}

type Store = Actions & State

const initialState: State = {
  callEndDialogDescription: '',
  displayCallEndDialog: false,
  displayContact: true,
  displayErrorDialog: false,
  errorCancelLabel: '',
  errorConfirmLabel: '',
  errorDescription: '',
  errorDialogElementRef: null,
  errorOnClose: undefined,
  errorOnConfirm: undefined,
  errorTitle: '',
  roomPanelOpen: false,
  roomPanelTitle: '',
  roomPanelType: null,
  rootElementHeight: 0,
  rootElementRef: null,
  rootElementWidth: 0,
  selectedMemberId: '',
  viewElementRef: null,
}

const stateCreatorFn: StateCreator<Store> = (set, get) => ({
  ...initialState,
  actions: {
    closeRoomPanel: () => {
      set({ roomPanelOpen: false })
    },
    createRootElementResizeObserver: element => {
      set({
        rootElementHeight: element.offsetHeight,
        rootElementWidth: element.offsetWidth,
      })

      const { disconnect, start, stop } = createResizeObserver(element, () => {
        set({
          rootElementHeight: element.offsetHeight,
          rootElementWidth: element.offsetWidth,
        })
      })

      return { disconnect, start, stop }
    },
    hideCallEndDialog: () => {
      set({ displayCallEndDialog: false })
    },
    hideContact: () => {
      set({ displayContact: false })
    },
    hideErrorDialog: () => {
      set({ displayErrorDialog: false })
    },
    openRoomPanel: () => {
      set({ roomPanelOpen: true })
    },
    resetRoomPanelState: () => {
      set({
        roomPanelOpen: false,
        roomPanelTitle: '',
        roomPanelType: null,
        selectedMemberId: '',
      })
    },
    setAdvancedPanel: memberId => {
      const { setRoomPanelType } = get().actions
      setRoomPanelType('advanced')
      set({ selectedMemberId: memberId })
    },
    setCallEndDialogContent: ({ description, onConfirm }) => {
      set({
        callEndDialogDescription: description,
        callEndDialogOnConfirm: onConfirm,
      })
    },
    setErrorDialogContent: ({
      cancelLabel = '',
      confirmLabel,
      description,
      onCancel,
      onConfirm,
      title,
    }) => {
      set({
        errorCancelLabel: cancelLabel,
        errorConfirmLabel: confirmLabel,
        errorDescription: description,
        errorOnClose: onCancel,
        errorOnConfirm: onConfirm,
        errorTitle: title,
      })
    },
    setErrorDialogRef: ref => {
      set({ errorDialogElementRef: ref })
    },
    setRoomPanelTitle: panelTitle => {
      set({
        roomPanelTitle: panelTitle,
      })
    },
    setRoomPanelType: panelType => {
      set({ roomPanelTitle: '', roomPanelType: panelType })
    },
    setRootElementRef: element => {
      set({
        rootElementRef: element,
      })
    },
    setViewElementRef: (ref: MutableRefObject<HTMLElement | null>) => {
      set({ viewElementRef: ref })
    },
    showCallEndDialog: () => {
      set({ displayCallEndDialog: true })
    },
    showContact: () => {
      set({ displayContact: true })
    },
    showErrorDialog: () => {
      set({ displayErrorDialog: true })
    },
  },
})

export const useUiStore = create<Store>()(stateCreatorFn)
export const useUiStoreActions = () => useUiStore.getState().actions

// store convenience functions
interface SubscribeToRootElementResizedParams {
  onResized?: () => void
  onUnsubscribe?: () => void
}

export const subscribeToRootElementResize = (
  params: SubscribeToRootElementResizedParams,
) => {
  const { onResized, onUnsubscribe } = params
  const unsubscribe = useUiStore.subscribe((state, prevState) => {
    if (!state.rootElementRef) {
      return // no root element
    }

    // fire callback when the root element's height or width changes
    const heightChanged =
      state.rootElementHeight !== prevState.rootElementHeight
    const widthChanged = state.rootElementWidth !== prevState.rootElementWidth

    if (heightChanged || widthChanged) {
      onResized?.()
    }
  })

  return () => {
    unsubscribe()
    onUnsubscribe?.()
  }
}
